import { useState } from 'react'
import type { CharacterWithStats } from '@shared/types'
import { HealthBar } from '@/components/PartyCard'
import { Button, NumberField, Panel, SectionHeading } from '@/components/ui'
import { cn } from '@/lib/cn'

type Patch = Partial<CharacterWithStats>

export function HealthPanel({
  character, canEdit, onPatch,
}: {
  character: CharacterWithStats
  canEdit: boolean
  onPatch: (patch: Patch) => void
}) {
  const [amount, setAmount] = useState('')
  const down = character.hpCurrent <= 0

  function apply(sign: 1 | -1) {
    const value = Math.abs(Number(amount) || 0)
    if (!value) return

    if (sign === -1) {
      // Damage eats temporary hit points first, per the 2024 rules.
      const fromTemp = Math.min(character.hpTemp, value)
      const toHp = value - fromTemp
      onPatch({
        hpTemp: character.hpTemp - fromTemp,
        hpCurrent: Math.max(-99, character.hpCurrent - toHp),
      })
    } else {
      onPatch({ hpCurrent: Math.min(character.hpMax, character.hpCurrent + value) })
    }
    setAmount('')
  }

  return (
    <Panel className="space-y-4 p-4">
      <SectionHeading
        action={
          canEdit && (
            <button
              type="button"
              onClick={() => onPatch({ heroicInspiration: !character.heroicInspiration })}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider transition-colors',
                character.heroicInspiration
                  ? 'border-gold-500 bg-gold-500 text-on-accent'
                  : 'border-ink-600 text-ink-500 hover:border-gold-600 hover:text-gold-400',
              )}
              aria-pressed={character.heroicInspiration}
            >
              Heroic Inspiration
            </button>
          )
        }
      >
        Hit points
      </SectionHeading>

      <div className="flex items-end justify-between gap-4">
        <p className="font-mono text-4xl leading-none tabular-nums">
          <span className={down ? 'text-blood-500' : 'text-ink-100'}>{character.hpCurrent}</span>
          <span className="text-xl text-ink-500"> / {character.hpMax}</span>
        </p>
        {character.hpTemp > 0 && (
          <p className="font-mono text-sm text-moss-500">+{character.hpTemp} temp</p>
        )}
      </div>

      <HealthBar current={character.hpCurrent} max={character.hpMax} temp={character.hpTemp} />

      {canEdit && (
        <div className="flex gap-2">
          <Button variant="danger" size="sm" onClick={() => apply(-1)} className="shrink-0">Damage</Button>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => { if (e.key === 'Enter') apply(-1) }}
            aria-label="Hit point change"
            placeholder="0"
            className="w-full rounded-lg border border-ink-600 bg-ink-900 px-2 py-1 text-center font-mono text-sm tabular-nums"
          />
          <Button size="sm" onClick={() => apply(1)} className="shrink-0 border-moss-500/40 text-moss-500 hover:bg-moss-900">
            Heal
          </Button>
        </div>
      )}

      {canEdit && (
        <div className="flex items-center gap-3 border-t border-ink-700/60 pt-3">
          <label className="text-xs uppercase tracking-wider text-ink-500" htmlFor="temp-hp">Temp HP</label>
          <NumberField
            id="temp-hp"
            min={0}
            max={999}
            fallback={0}
            value={character.hpTemp}
            onChange={(hpTemp) => onPatch({ hpTemp })}
            className="w-16 rounded-lg"
          />

          <label className="ml-auto text-xs uppercase tracking-wider text-ink-500" htmlFor="exhaustion">Exhaustion</label>
          <NumberField
            id="exhaustion"
            min={0}
            max={6}
            fallback={0}
            value={character.exhaustion}
            onChange={(exhaustion) => onPatch({ exhaustion })}
            className={cn('w-14 rounded-lg', character.exhaustion > 0 && 'border-blood-500/50 text-blood-500')}
          />
        </div>
      )}

      {down && (
        <DeathSaves
          saves={character.deathSaves}
          canEdit={canEdit}
          onChange={(deathSaves) => onPatch({ deathSaves })}
        />
      )}
    </Panel>
  )
}

function DeathSaves({
  saves, canEdit, onChange,
}: {
  saves: { successes: number; failures: number }
  canEdit: boolean
  onChange: (value: { successes: number; failures: number }) => void
}) {
  return (
    <div className="space-y-2 border-t border-ink-700/60 pt-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blood-500">Death saves</p>
      <Track
        label="Successes"
        count={saves.successes}
        tone="moss"
        canEdit={canEdit}
        onSet={(successes) => onChange({ ...saves, successes })}
      />
      <Track
        label="Failures"
        count={saves.failures}
        tone="blood"
        canEdit={canEdit}
        onSet={(failures) => onChange({ ...saves, failures })}
      />
    </div>
  )
}

function Track({
  label, count, tone, canEdit, onSet,
}: {
  label: string
  count: number
  tone: 'moss' | 'blood'
  canEdit: boolean
  onSet: (value: number) => void
}) {
  const fill = tone === 'moss' ? 'border-moss-500 bg-moss-500' : 'border-blood-500 bg-blood-500'
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 text-xs text-ink-400">{label}</span>
      <div className="flex gap-1.5">
        {[1, 2, 3].map((index) => {
          const filled = count >= index
          const props = {
            className: cn('size-4 rounded-full border transition-colors', filled ? fill : 'border-ink-600'),
            'aria-label': `${label} ${index}`,
          }
          // Clicking a filled pip clears back to just below it, so mistakes undo.
          return canEdit ? (
            <button key={index} type="button" {...props} aria-pressed={filled} onClick={() => onSet(filled ? index - 1 : index)} />
          ) : (
            <span key={index} {...props} />
          )
        })}
      </div>
    </div>
  )
}
